import React from 'react';
import { Typography, Box, Container, Link, Paper, Alert, AlertTitle } from '@mui/material';
import { motion } from 'framer-motion';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import GavelIcon from '@mui/icons-material/Gavel';
import SecurityIcon from '@mui/icons-material/Security';

export const Legal: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <Paper elevation={3} sx={{ p: 4, backgroundColor: '#16181C' }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#E7E9EA', mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <GavelIcon sx={{ fontSize: 32 }} />
              Terms of Service & Privacy Policy
            </Typography>

            <Box sx={{ mb: 6 }}>
              <motion.div variants={itemVariants}>
                <Alert 
                  severity="warning" 
                  variant="outlined"
                  sx={{
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    '& .MuiAlert-icon': {
                      color: '#F59E0B'
                    }
                  }}
                >
                  <AlertTitle sx={{ color: '#F59E0B', fontWeight: 'bold' }}>
                    Important Notice Regarding User Responsibility
                  </AlertTitle>
                  <Typography sx={{ color: '#E7E9EA' }}>
                    By using X Media Downloader, you explicitly acknowledge and accept full responsibility for:
                    <ul>
                      <li>Compliance with X's Terms of Service</li>
                      <li>Adherence to all applicable laws and regulations</li>
                      <li>Proper use and handling of downloaded content</li>
                      <li>Any consequences resulting from your use of this tool</li>
                    </ul>
                  </Typography>
                </Alert>
              </motion.div>
            </Box>

            <motion.div variants={itemVariants}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1D9BF0', display: 'flex', alignItems: 'center', gap: 2 }}>
                <GavelIcon />
                Comprehensive Terms of Service
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#E7E9EA', mb: 2 }}>
                  1. Acceptance of Terms
                </Typography>
                <Typography variant="body1" sx={{ color: '#E7E9EA', mb: 2, pl: 3 }}>
                  By accessing or using X Media Downloader, you agree to be bound by these Terms of Service, all applicable laws and regulations, and agree that you are responsible for compliance with any applicable local laws. If you do not agree with any of these terms, you are prohibited from using or accessing this service.
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#E7E9EA', mb: 2 }}>
                  2. Platform Compliance
                </Typography>
                <Typography variant="body1" sx={{ color: '#E7E9EA', mb: 2, pl: 3 }}>
                  You must comply with:
                  <ul>
                    <li>X's Terms of Service (<Link href="https://twitter.com/tos" target="_blank" sx={{ color: '#1D9BF0' }}>https://twitter.com/tos</Link>)</li>
                    <li>X's Developer Agreement and Policy</li>
                    <li>X's Content Redistribution Rules</li>
                    <li>Any applicable rate limits or technical restrictions</li>
                  </ul>
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#E7E9EA', mb: 2 }}>
                  3. Content Usage & Intellectual Property
                </Typography>
                <Typography variant="body1" sx={{ color: '#E7E9EA', mb: 2, pl: 3 }}>
                  When downloading content:
                  <ul>
                    <li>Respect copyright and intellectual property rights</li>
                    <li>Only download content you have permission to access</li>
                    <li>Do not use downloaded content for commercial purposes without proper authorization</li>
                    <li>Maintain attribution to original content creators</li>
                    <li>Do not modify or distribute content in ways that violate original terms</li>
                  </ul>
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box sx={{ mb: 4 }}>
                <Alert 
                  severity="info" 
                  variant="outlined"
                  sx={{
                    backgroundColor: 'rgba(29, 155, 240, 0.1)',
                    border: '1px solid rgba(29, 155, 240, 0.3)',
                    '& .MuiAlert-icon': {
                      color: '#1D9BF0'
                    }
                  }}
                >
                  <AlertTitle sx={{ color: '#1D9BF0' }}>Usage Restrictions</AlertTitle>
                  <Typography sx={{ color: '#E7E9EA' }}>
                    This tool is strictly prohibited for:
                    <ul>
                      <li>Commercial use without explicit authorization</li>
                      <li>Mass downloading or automated scraping</li>
                      <li>Redistribution of downloaded content</li>
                      <li>Circumvention of platform security measures</li>
                      <li>Any illegal or unauthorized purposes</li>
                    </ul>
                  </Typography>
                </Alert>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1D9BF0', display: 'flex', alignItems: 'center', gap: 2 }}>
                <SecurityIcon />
                Privacy Policy
              </Typography>
              
              <Alert 
                severity="info" 
                variant="outlined"
                sx={{
                  backgroundColor: 'rgba(29, 155, 240, 0.1)',
                  border: '1px solid rgba(29, 155, 240, 0.3)',
                  mb: 4,
                  '& .MuiAlert-icon': {
                    color: '#1D9BF0'
                  }
                }}
              >
                <AlertTitle sx={{ color: '#1D9BF0' }}>Data Collection & Storage</AlertTitle>
                <Typography sx={{ color: '#E7E9EA' }}>
                  X Media Downloader operates with a strict no-logs policy:
                  <ul>
                    <li>We do not collect or store personal information</li>
                    <li>No download history is saved on our servers</li>
                    <li>All operations are performed client-side</li>
                    <li>No tracking or analytics are implemented</li>
                  </ul>
                </Typography>
              </Alert>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography variant="body1" sx={{ color: '#E7E9EA', mb: 4 }}>
                While using X Media Downloader:
                <ul>
                  <li>Your interactions with X's services are subject to <Link href="https://twitter.com/privacy" target="_blank" sx={{ color: '#1D9BF0' }}>X's Privacy Policy</Link></li>
                  <li>Downloaded content is saved directly to your device</li>
                  <li>We have no access to or control over downloaded content</li>
                  <li>Local storage is used only for essential functionality</li>
                </ul>
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1D9BF0', display: 'flex', alignItems: 'center', gap: 2 }}>
                <WarningIcon />
                Disclaimer
              </Typography>
              
              <Alert
                severity="warning" 
                variant="outlined"
                sx={{
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  '& .MuiAlert-icon': {
                    color: '#F59E0B'
                  }
                }}
              >
                <AlertTitle sx={{ color: '#F59E0B' }}>Service Limitations</AlertTitle>
                <Typography sx={{ color: '#E7E9EA' }}>
                  X Media Downloader is provided "as is" without any warranties:
                  <ul>
                    <li>No guarantee of service availability or reliability</li>
                    <li>No responsibility for content accuracy or quality</li>
                    <li>No liability for any damages or losses</li>
                    <li>Service may be modified or terminated at any time</li>
                  </ul>
                </Typography>
              </Alert>
            </motion.div>
          </Paper>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Legal; 